import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import get from 'lodash/get';
import trim from 'lodash/trim';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useTracking } from '../../lib/TrackingContext';
import { subscribeNewsletter } from '../../lib/requests';

import Button from '../buttons/Button';

import styles from '../../../styles/partials/newsletter.module.css';

interface NewsletterProps {
    email?: string | null;
    withoutLetter?: boolean;
    red?: boolean;
    small?: boolean;
    large?: boolean;
    onSubscribed?: (() => void) | null;
    className?: string | null;
}

function Newsletter({
    email: initialEmail = null,
    withoutLetter = false,
    red = false,
    small = false,
    large = false,
    onSubscribed = null,
    className = null,
}: NewsletterProps) {
    const [email, setEmail] = useState(initialEmail);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [subscribed, setSubscribed] = useState(false);

    const route = useUrlGenerator();
    const { trackEvent } = useTracking();

    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setError(null);
    }, []);

    const onSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();

            if (email === null || trim(email).length < 1) {
                return;
            }

            setLoading(true);
            setError(null);

            const url = route('api.newsletter');
            subscribeNewsletter(url, email)
                .then(({ success }: { success: boolean }) => {
                    if (onSubscribed !== null) {
                        if (!success) {
                            setError("Il s'est produit une erreur.");
                        } else {
                            onSubscribed();
                        }
                    } else {
                        setEmail(null);
                        setLoading(false);
                        setSubscribed(success);
                        setError(!success ? "Il s'est produit une erreur." : null);
                    }

                    if (success) {
                        trackEvent('Newsletter', 'register', email || '');
                    }
                })
                .catch((err: any) => {
                    if (err.name === 'ValidationError') {
                        setError(get(err.responseData, 'errors.email.0', null));
                    } else {
                        setError("Il s'est produit une erreur.");
                    }
                    setLoading(false);
                });
        },
        [email, route, onSubscribed, trackEvent],
    );

    const action = 'https://link.yapla.org/ngs/1600255/D7cxeo5fviuFoq6YZzr5/embedded';

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.small]: small,
                    [styles.large]: large,
                    [styles.hasErrors]: error !== null,
                    [styles.isLoading]: loading,
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.content}>
                {large ? (
                    <h1 className={styles.title}>
                        <FormattedMessage
                            defaultMessage="Abonnez-vous à notre infolettre"
                            description="Newsletter subscription title"
                        />
                    </h1>
                ) : (
                    <h2 className={styles.title}>
                        <FormattedMessage
                            defaultMessage="Abonnez-vous à notre infolettre"
                            description="Newsletter subscription title"
                        />
                    </h2>
                )}
                <p className={styles.description}>
                    <FormattedMessage
                        defaultMessage="Recevez à chaque mois des propositions d'activités et des nouvelles"
                        description="Newsletter subscription description"
                    />
                </p>
                {!withoutLetter ? <div className={styles.letter} /> : null}
            </div>
            {!subscribed ? (
                <form action={action} method="POST" className={styles.form}>
                    <div className={styles.cols}>
                        <div className={styles.col}>
                            <input
                                required
                                type="email"
                                name="email"
                                className={styles.input}
                                value={email || ''}
                                placeholder="Entrez votre courriel..."
                                onChange={onChange}
                            />
                            {error !== null ? <div className={styles.error}>{error}</div> : null}
                        </div>
                        <div className={styles.col}>
                            <Button type="submit" className={styles.button} red={red}>
                                <FormattedMessage
                                    defaultMessage="S'abonner"
                                    description="Subscribe button label"
                                />
                            </Button>
                        </div>
                    </div>
                </form>
            ) : (
                <div className={styles.success}>
                    <FormattedMessage
                        defaultMessage="Vous êtes maintenant abonné à notre infolettre!"
                        description="Newsletter subscription success message"
                    />
                </div>
            )}
        </div>
    );
}

export default Newsletter;
